import { AlertTriangle, Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import { AutocompleteTextarea } from './AutocompleteTextarea';
import type { EditorMode, SuggestModel } from '../lib/autocomplete';

/** A monospace editor with a live compile-status badge, inline error, and autocomplete. */
export function CodeField({
  label,
  hint,
  value,
  onChange,
  error,
  touched,
  rows = 4,
  placeholder,
  mode,
  model,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  /** Compile error from the live preview, if any. */
  error?: string;
  /** Whether the value is non-empty (so we don't flag an untouched field). */
  touched: boolean;
  rows?: number;
  placeholder?: string;
  /** Which language the field holds — drives autocomplete. */
  mode: EditorMode;
  model: SuggestModel;
}) {
  const state = !touched ? 'idle' : error ? 'error' : 'ok';

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="eyebrow">
          {label}
          {hint && <span className="ml-1.5 normal-case tracking-normal text-text-faint">· {hint}</span>}
        </label>
        {state === 'ok' && (
          <span className="inline-flex items-center gap-1 font-condensed text-[9px] uppercase tracking-wider text-ok">
            <Check size={10} /> compiles
          </span>
        )}
        {state === 'error' && (
          <span className="inline-flex items-center gap-1 font-condensed text-[9px] uppercase tracking-wider text-crit">
            <AlertTriangle size={10} /> error
          </span>
        )}
      </div>
      <AutocompleteTextarea
        value={value}
        onChange={onChange}
        mode={mode}
        model={model}
        rows={rows}
        placeholder={placeholder}
        className={cn(
          'w-full resize-y border bg-surface-sunken px-2.5 py-2 font-mono text-[12px] leading-[1.55] text-text',
          'placeholder:text-text-faint focus:outline-none',
          state === 'error' ? 'border-crit/50 focus:border-crit' : 'border-hairline focus:border-accent',
        )}
      />
      {error && <p className="mt-1 font-mono text-[11px] leading-snug text-crit">{error}</p>}
    </div>
  );
}
